import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Target, AlertTriangle } from 'lucide-react';
import { brandDependencyData } from './dependencyData';
import { chartBlue, chartGreen } from '@/utils/chartColors';

// Risk Level Badge
const RiskBadge = ({ level }) => {
  const config = {
    low: { bg: 'bg-stable/20', text: 'text-stable', border: 'border-stable/30', label: 'Low Risk' },
    medium: { bg: 'bg-warning/20', text: 'text-warning', border: 'border-warning/30', label: 'Medium Risk' },
    high: { bg: 'bg-destructive/20', text: 'text-destructive', border: 'border-destructive/30', label: 'High Risk' },
  };
  const c = config[level] || config.medium;
  return (
    <Badge className={`${c.bg} ${c.text} ${c.border} border font-semibold`}>
      {c.label}
    </Badge>
  );
};

// Custom Tooltip for Donut Chart
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg p-3">
      <p className="font-semibold text-foreground">{payload[0].name}</p>
      <p className="text-lg font-bold text-primary">{payload[0].value.toFixed(1)}%</p>
    </div>
  );
};

export const ConcentrationOverview = () => {
  const { concentration, players } = brandDependencyData;
  
  // Donut chart data
  const chartData = [
    { name: `Top 1 (${players[0].name})`, value: concentration.top1Percent, fill: chartBlue() },
    { name: 'Top 2-3 Players', value: concentration.top3Percent - concentration.top1Percent, fill: chartGreen() },
    { name: 'Remaining Roster', value: concentration.remainingPercent, fill: 'hsl(var(--neutral-slate))' },
  ];

  return (
    <section className="mb-10" data-testid="concentration-overview-section">
      <div className="flex items-center gap-3 mb-6">
        <Target className="w-6 h-6 text-primary" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Brand Concentration Overview</h2>
      </div>
      
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Donut Chart */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Team Brand Value Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="h-64" data-testid="concentration-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Concentration Metrics */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Concentration Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            {/* Top 1 Player */}
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Top 1 Player Concentration</span>
                <span className="text-2xl font-bold text-primary">{concentration.top1Percent}%</span>
              </div>
              <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary rounded-full"
                  style={{ width: `${concentration.top1Percent}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">{players[0].name}</p>
            </div>
            
            {/* Top 3 Players */}
            <div className="p-4 rounded-lg bg-muted/20 border border-border/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Top 3 Players Concentration</span>
                <span className="text-xl font-bold text-foreground">{concentration.top3Percent}%</span>
              </div>
              <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-stable rounded-full"
                  style={{ width: `${concentration.top3Percent}%` }}
                />
              </div>
            </div>
            
            {/* BCI Index */}
            <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                  <span className="text-sm font-medium text-foreground">Brand Concentration Index (BCI)</span>
                </div>
                <RiskBadge level={concentration.riskLevel} />
              </div>
              <div className="flex items-end gap-2">
                <span className="text-3xl font-bold text-destructive">{concentration.brandConcentrationIndex}</span>
                <span className="text-sm text-muted-foreground mb-1">/ 100</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Optimal threshold: &lt;{concentration.optimalThreshold}% in single player
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ConcentrationOverview;
